import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import { withStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import Divider from "@material-ui/core/Divider";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Slider from "@material-ui/core/Slider";
import Switch from "@material-ui/core/Switch";
import Typography from "@material-ui/core/Typography";

import brandLogoUrls, { normalizeBrandLogoKey } from "../common/logoUrls";
import { formatIls } from "../list/tirePairModel";
import { getVisiblePairFilterSections } from "./tirePairFilters";

const TYPE_OPTIONS = [
  { value: "all", label: "הכל", countKey: "totalCount" },
  { value: "set", label: "סטים מקוריים", countKey: "setCount" },
  { value: "pair", label: "זוגות תואמים", countKey: "pairCount" },
];

const styles = (theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 12,
    padding: theme.spacing(2),
  },
  plain: {
    backgroundColor: "transparent",
    border: "none",
    borderRadius: 0,
    padding: 0,
  },
  section: {
    marginBottom: theme.spacing(2.5),
    "&:last-child": {
      marginBottom: 0,
    },
  },
  divider: {
    marginBottom: theme.spacing(1.5),
  },
  segment: {
    display: "flex",
    flexDirection: "column",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    overflow: "hidden",
  },
  segmentButton: {
    borderRadius: 0,
    borderBottom: `1px solid ${theme.palette.divider}`,
    transition: "background-color 0.15s ease-in-out",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  segmentLabel: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  priceLabels: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: theme.spacing(1),
  },
  brandRow: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    boxSizing: "border-box",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    marginBottom: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingLeft: theme.spacing(0.5),
    backgroundColor: theme.palette.background.paper,
    cursor: "pointer",
    transition: "background-color 0.15s ease-in-out, border-color 0.15s ease-in-out",
    "&:last-child": {
      marginBottom: 0,
    },
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  brandRowSelected: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
  brandCheck: {
    padding: theme.spacing(1),
  },
  brandMain: {
    flex: "1 1 auto",
    minWidth: 0,
    display: "flex",
    alignItems: "center",
  },
  brandLogo: {
    display: "block",
    maxWidth: 92,
    maxHeight: 22,
    objectFit: "contain",
  },
});

class TirePairFiltersPanel extends React.Component {
  handleBrandToggle = (brand) => {
    const key = String(brand || "").trim().toLowerCase();
    const current = this.props.filters.brands || [];
    const next = current.indexOf(key) === -1
      ? current.concat(key)
      : current.filter((item) => item !== key);

    this.props.onChange({ brands: next });
  };

  handlePriceChange = (event, value) => {
    if (this.props.onPriceChange) {
      this.props.onPriceChange(value);
    }
  };

  handlePriceCommitted = (event, value) => {
    if (this.props.onPriceCommitted) {
      this.props.onPriceCommitted(value);
    }
  };

  renderSection(title, children) {
    const { classes } = this.props;

    return (
      <section className={classes.section}>
        <Typography variant="subtitle2" component="h2" gutterBottom>
          {title}
        </Typography>
        <Divider className={classes.divider} />
        {children}
      </section>
    );
  }

  renderType() {
    const { classes, facets, filters } = this.props;

    return this.renderSection(
      "סוג",
      <div className={classes.segment} role="group" aria-label="סוג">
        {TYPE_OPTIONS.map((option) => {
          const selected = filters.type === option.value;
          return (
            <Button
              key={option.value}
              fullWidth
              disableElevation
              variant={selected ? "contained" : "text"}
              color={selected ? "primary" : "default"}
              className={classes.segmentButton}
              classes={{ label: classes.segmentLabel }}
              onClick={() => this.props.onChange({ type: option.value })}
              aria-pressed={selected}
            >
              <span>{option.label}</span>
              <Typography
                variant="caption"
                component="span"
                color={selected ? "inherit" : "textSecondary"}
              >
                {facets[option.countKey] || 0}
              </Typography>
            </Button>
          );
        })}
      </div>
    );
  }

  renderAvailability() {
    const { facets, filters } = this.props;

    return this.renderSection(
      "זמינות",
      <FormControlLabel
        control={
          <Switch
            color="primary"
            checked={!!filters.localStockOnly}
            onChange={(event) => this.props.onChange({ localStockOnly: event.target.checked })}
          />
        }
        label={
          <Typography variant="body2" component="span">
            {`במלאי בארץ בלבד (${facets.localStockCount})`}
          </Typography>
        }
      />
    );
  }

  renderPrice() {
    const { classes, facets, sliderValue } = this.props;
    if (!sliderValue) {
      return null;
    }

    return this.renderSection(
      "מחיר לסט",
      <React.Fragment>
        <div className={classes.priceLabels}>
          <Typography variant="body2">{formatIls(sliderValue[0])}</Typography>
          <Typography variant="body2">{formatIls(sliderValue[1])}</Typography>
        </div>
        <Slider
          min={facets.priceMin}
          max={facets.priceMax}
          step={1}
          value={sliderValue}
          onChange={this.handlePriceChange}
          onChangeCommitted={this.handlePriceCommitted}
          valueLabelDisplay="off"
          color="primary"
          getAriaLabel={(index) => (index === 0 ? "מחיר מינימום" : "מחיר מקסימום")}
        />
      </React.Fragment>
    );
  }

  renderBrands() {
    const { classes, facets, filters } = this.props;
    const selected = filters.brands || [];

    return this.renderSection(
      "מותג",
      <div>
        {facets.brands.map((item) => {
          const key = item.brand.toLowerCase();
          const checked = selected.indexOf(key) !== -1;
          const logo = brandLogoUrls[normalizeBrandLogoKey(item.brand)];

          return (
            <label
              key={key}
              className={classNames(classes.brandRow, checked && classes.brandRowSelected)}
            >
              <Checkbox
                color="primary"
                checked={checked}
                onChange={() => this.handleBrandToggle(item.brand)}
                className={classes.brandCheck}
              />
              <span className={classes.brandMain}>
                {logo ? (
                  <img src={logo} alt={item.brand} className={classes.brandLogo} />
                ) : (
                  <Typography variant="body2">{item.brand}</Typography>
                )}
              </span>
              <Typography variant="caption" color="textSecondary">
                {item.count}
              </Typography>
            </label>
          );
        })}
      </div>
    );
  }

  render() {
    const { classes, variant } = this.props;
    const sections = getVisiblePairFilterSections(this.props.facets);

    if (!sections.any) {
      return null;
    }

    return (
      <div className={classNames(classes.root, variant === "plain" && classes.plain)}>
        {sections.type ? this.renderType() : null}
        {sections.availability ? this.renderAvailability() : null}
        {sections.price ? this.renderPrice() : null}
        {sections.brands ? this.renderBrands() : null}
      </div>
    );
  }
}

TirePairFiltersPanel.propTypes = {
  classes: PropTypes.object.isRequired,
  filters: PropTypes.shape({
    brands: PropTypes.arrayOf(PropTypes.string),
    localStockOnly: PropTypes.bool,
    type: PropTypes.string,
    minPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
    maxPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
  }).isRequired,
  facets: PropTypes.shape({
    brands: PropTypes.arrayOf(PropTypes.shape({
      brand: PropTypes.string,
      count: PropTypes.number,
    })),
    priceMin: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
    priceMax: PropTypes.oneOfType([PropTypes.number, PropTypes.oneOf([null])]),
    setCount: PropTypes.number,
    pairCount: PropTypes.number,
    localStockCount: PropTypes.number,
    totalCount: PropTypes.number,
  }).isRequired,
  sliderValue: PropTypes.arrayOf(PropTypes.number),
  onChange: PropTypes.func.isRequired,
  onPriceChange: PropTypes.func,
  onPriceCommitted: PropTypes.func,
  variant: PropTypes.oneOf(["card", "plain"]),
};

TirePairFiltersPanel.defaultProps = {
  sliderValue: null,
  variant: "card",
};

export default withStyles(styles)(TirePairFiltersPanel);
